import * as React from 'react'
import * as classnames from 'classnames'
import { isEmpty } from 'lodash-es'
import EnhancedLazyLoad from '../../../enhancedLazyLoad'
import ImagePlaceholder from '../../../imagePlaceholder'
import ImagePreview from '../../../imagePreview'
import ui from '../../../../../common/constants/ui'
import {
  mediaQuery,
  srcSetMappings,
  selectImage,
  buildImageSrcSet,
  ISrcSetImages
} from '../../../../../common/utils/imageSrcSetHelpers'
import CardDetails from '../cardDetails/cardDetails'
import CardPreview from '../cardPreview/cardPreview'
import { IPromoData, IImageRatio } from '../elementsPromoItemsBlock'

import * as styles from './card.module.css'

type Omit<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>

interface ICard extends Omit<IPromoData, 'marketID'> {
  width: number
  imageRatios: IImageRatio
  imageURL?: string
  onClick?: () => any
  showCTA?: boolean
}

const getDefaultImage = (image_urls: ISrcSetImages[]) => {
  if (!isEmpty(image_urls)) {
    const selectedImage = selectImage('w500', image_urls)

    if (selectedImage) {
      return selectedImage.url
    } else {
      return image_urls[image_urls.length - 1].url
    }
  } else {
    return ''
  }
}

const getImgSet = ({
  image_urls,
  imageRatios,
  width,
  alt
}: {
  image_urls: ISrcSetImages[]
  imageRatios: IImageRatio
  width: number
  alt: string
}) => {
  const srcSet = buildImageSrcSet(srcSetMappings, image_urls)
  const defaultImageUrl = getDefaultImage(image_urls)
  const imagePlaceholderRatio = 1 / imageRatios.previewRatio
  const ratioMultiplier =
    imageRatios.imageRatio && imageRatios.imageRatio > imagePlaceholderRatio
      ? imageRatios.imageRatio / imagePlaceholderRatio
      : 1

  // 32px - size of gutter between items
  const itemWidth = Math.round(width * ratioMultiplier * 100) / 100
  const maxWidthSize = Math.round((1408 * 0.75 - 96) * itemWidth) / 100
  const sizes = [
    `${mediaQuery(1408)} ${maxWidthSize}px`,
    `${mediaQuery(1295)} calc(${
      Math.round(itemWidth * 0.75 * 100) / 100
    }vw - 96px)`,
    `${mediaQuery(832)} calc(${itemWidth}vw - 96px)`,
    `${mediaQuery(640)} calc(${itemWidth}vw - 64px)`,
    `calc(${ratioMultiplier * 100}vw - 32px)`
  ].join(', ')

  return (
    <img
      sizes={sizes}
      srcSet={srcSet}
      src={defaultImageUrl}
      alt={alt}
      className={classnames(styles.image, styles.imageResponsive)}
    />
  )
}

const getSimpleImg = (previews: IPromoData['previews'], alt: string) => (
  <ImagePreview previews={previews} alt={alt} className={styles.image} />
)

const Card = ({
  previews,
  title,
  author,
  itemURL,
  categoryName,
  width,
  imageRatios,
  image_urls,
  onClick,
  showCTA
}: ICard) => {
  const alt = `${title} by ${author}`
  const image =
    image_urls && !isEmpty(image_urls)
      ? getImgSet({ image_urls, imageRatios, width, alt })
      : getSimpleImg(previews, alt)

  return (
    <a
      className={styles.root}
      href={itemURL}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`View ${title}`}
      onClick={() => onClick && onClick()}
    >
      <CardPreview categoryName={categoryName}>
        <EnhancedLazyLoad
          offset={ui.lazyLoadOffset}
          placeholder={<ImagePlaceholder ratio={imageRatios.previewRatio} />}
        >
          <ImagePlaceholder ratio={imageRatios.previewRatio}>
            {image}
          </ImagePlaceholder>
        </EnhancedLazyLoad>
      </CardPreview>
      <CardDetails
        title={title}
        author={author}
        categoryName={categoryName}
        showCTA={showCTA}
      />
    </a>
  )
}

export default Card
